import { cs } from '../../../lib/currency';
import { useState, useEffect, useCallback } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import { Loader2, AlertTriangle, Trash2, RefreshCw, Search } from 'lucide-react';
import api from '../../../services/api';
import { toast } from 'sonner';

/**
 * "Find duplicate lane bookings" panel — opens a modal that calls
 * `GET /admin/invoices/lane-booking/find-duplicates`, lists the groups
 * (rows that share customer_id+lane_id+date+start_time), and lets admin
 * delete individual duplicate rows with a confirm step + audit reason.
 *
 * Triggered from the InvoiceManagement page's secondary actions bar.
 */
const LaneDuplicatesDialog = ({ open, onOpenChange, onCleanupComplete }) => {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null); // { row, group }
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [runningCleanup, setRunningCleanup] = useState(false);
  const [cleanupSummary, setCleanupSummary] = useState(null);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/invoices/lane-booking/find-duplicates');
      setGroups(data.groups || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load duplicates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchGroups();
      setCleanupSummary(null);
    } else {
      setGroups([]); setConfirmDelete(null); setReason('');
    }
  }, [open, fetchGroups]);

  const runCleanup = async () => {
    setRunningCleanup(true);
    try {
      const { data } = await api.post('/admin/invoices/lane-booking/run-cleanup');
      setCleanupSummary(data);
      const deleted = (data.deleted_expired_session || 0) + (data.deleted_never_paid || 0);
      const forwarded = data.forward_paid || 0;
      toast.success(
        deleted + forwarded === 0
          ? 'Nothing to clean up — all rows look healthy.'
          : `Cleaned up ${deleted} abandoned + finalized ${forwarded} stuck-paid row(s).`
      );
      fetchGroups();
      onCleanupComplete?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally {
      setRunningCleanup(false);
    }
  };

  const askDelete = (group, row) => {
    setConfirmDelete({ group, row });
    setReason('');
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    if (reason.trim().length < 5) {
      toast.error('Please enter a reason (≥ 5 chars) for the audit log.');
      return;
    }
    setDeleting(true);
    try {
      await api.post('/admin/invoices/lane-booking/delete-duplicate', {
        booking_id: confirmDelete.row.id,
        reason: reason.trim(),
      });
      toast.success(
        confirmDelete.row.payment_status === 'paid'
          ? 'Duplicate (PAID) booking deleted — payment_transactions marked orphaned.'
          : 'Duplicate booking deleted.'
      );
      setConfirmDelete(null);
      setReason('');
      fetchGroups();
      onCleanupComplete?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="lane-duplicates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5 text-orange-600" />
              Find &amp; Resolve Duplicate Lane Bookings
            </DialogTitle>
            <DialogDescription>
              Groups two or more <code>lane_bookings</code> rows that share the same customer, lane, date, and start time —
              a strong signal of duplicate inserts (typically from abandoned-then-retried Stripe checkouts).
              Delete the duplicate(s) you don't want to keep.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 my-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={runCleanup}
                disabled={runningCleanup}
                data-testid="run-lane-cleanup-btn"
              >
                {runningCleanup ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Run auto-cleanup
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={fetchGroups}
                disabled={loading}
                data-testid="refresh-duplicates-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Refresh list
              </Button>
              <span className="text-xs text-slate-500 ml-auto">
                {groups.length === 0 ? '' : `${groups.length} duplicate group${groups.length === 1 ? '' : 's'}`}
              </span>
            </div>

            {cleanupSummary && (
              <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 font-mono">
                Cleanup result: forward_paid={cleanupSummary.forward_paid},
                deleted_expired_session={cleanupSummary.deleted_expired_session},
                deleted_never_paid={cleanupSummary.deleted_never_paid},
                checked={cleanupSummary.checked}, errors={cleanupSummary.errors}
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-center py-10 text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Scanning…
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-10 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg">
              <span className="font-medium">No duplicate lane bookings found.</span>
              <p className="text-xs text-slate-600 mt-1">Try "Run auto-cleanup" first if you suspect there are abandoned pending rows.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {groups.map((g, gi) => (
                <div key={gi} className="border border-orange-200 bg-orange-50/40 rounded-lg p-3" data-testid={`duplicate-group-${gi}`}>
                  <div className="text-sm font-semibold text-slate-800">
                    {g.customer_name || g.customer_email || g.customer_id}
                    <span className="text-slate-500 font-normal"> · {g.lane_id} · {g.date} at {g.start_time}</span>
                  </div>
                  <div className="text-xs text-slate-500 mb-2">{g.count} duplicate rows</div>
                  <div className="space-y-1.5">
                    {g.rows.map((r) => (
                      <div key={r.id} className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded px-2 py-1.5 text-xs">
                        <div className="flex-1 min-w-0 truncate">
                          <span className="font-mono text-slate-500">{r.id?.slice(0, 8)}…</span>
                          <span className="ml-2">{r.start_time}–{r.end_time}</span>
                          <span className="ml-2 font-medium">{cs()}{r.price}</span>
                          {r.payment_status === 'paid' ? (
                            <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-[10px]">Paid {r.paid_at?.slice(0, 10)}</Badge>
                          ) : (
                            <Badge className="ml-2 bg-amber-100 text-amber-800 text-[10px]">{r.payment_status || 'pending'}</Badge>
                          )}
                          <span className="ml-2 text-slate-400">created {r.created_at?.slice(0, 10)}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:bg-red-50 h-7"
                          onClick={() => askDelete(g, r)}
                          data-testid={`delete-duplicate-row-${r.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          Delete
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => { if (!o) { setConfirmDelete(null); setReason(''); } }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete duplicate lane booking?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm space-y-2">
                <p>This will physically remove the <code>lane_bookings</code> row. The row will be snapshotted in <code>lane_bookings_deleted_log</code> so it can be recovered manually if needed.</p>
                {confirmDelete?.row?.payment_status === 'paid' && (
                  <p className="bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">
                    ⚠️ This booking is marked <strong>PAID</strong>. Any matching <code>payment_transactions</code> row will be marked <code>orphaned_by_admin_delete</code> (for audit) but the actual Stripe charge is <strong>not</strong> refunded automatically.
                  </p>
                )}
                <div className="mt-2">
                  <label className="text-xs font-medium text-slate-700 block mb-1">
                    Reason (audit log, required) <span className="text-slate-400 font-normal">≥ 5 chars</span>
                  </label>
                  <Textarea
                    rows={2}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Duplicate created by retry after abandoned checkout. Keeping the row paid 2026-05-08."
                    data-testid="delete-duplicate-reason"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => { e.preventDefault(); doDelete(); }}
              disabled={deleting || reason.trim().length < 5}
              data-testid="confirm-delete-duplicate"
            >
              {deleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default LaneDuplicatesDialog;
